/**
 * Schematic corridor view (PRD §5.8 S-07).
 *
 * Subway-map-style horizontal flow of pipeline stations. Designed for
 * glance-and-go briefing-room assessment — operators can see the entire
 * 200 km corridor's health in one row at a glance.
 *
 *   ●━━━●━━━◆━━━●━━━●━━━●━━━●━━━●━━━◎━━━●━━━●━━━●━━━●━━━●━━━●━━━◆━━━●━━━●━━━●━━━●
 *  KP-0     KP-50               KP-95                                KP-140         KP-200
 *  Segamat  (valve)             (scraper)                            (valve)        Pengerang
 *
 * Icon shapes per spec:
 *   - default station:  filled circle
 *   - valve station:    diamond (square rotated 45°)
 *   - scraper station:  bordered circle with double-ring
 *
 * Colour bands per station state:
 *   - normal      → emerald  (no incidents in last 24h)
 *   - watch       → amber    (1+ DRAFT incidents OR correlation_pattern set)
 *   - active      → rose     (1+ ACTIVE / ACKNOWLEDGED / INVESTIGATING)
 *
 * Click a station → emits `onSegmentClick(segmentId)`.
 * Landmarks (valve stations + scraper) are read from
 * `site.metadata.main_valve_stations[].kp` and
 * `site.metadata.scraper_stations[].kp` — never hard-coded.
 */

import { useMemo } from "react";
import type { IncidentDto } from "@/lib/types";

export type StationKind = "default" | "valve" | "scraper";
export type StationState = "normal" | "watch" | "active";

export interface SchematicSegment {
  segmentId: string;
  /** km from datum (start_chainage in DB). Used to position + label. */
  startChainage: number;
  /** Optional human label — falls back to segmentId. */
  name?: string | null;
}

export interface SchematicLandmarks {
  /** KP values where main valve stations sit (e.g. [50, 140] for PGU South). */
  valveKps: number[];
  /** KP values where scraper stations sit (e.g. [95] for PGU South). */
  scraperKps: number[];
}

export interface SchematicCorridorProps {
  segments: SchematicSegment[];
  incidents: Pick<IncidentDto, "id" | "segmentId" | "status">[];
  landmarks?: SchematicLandmarks;
  /** Called when the operator clicks a station. */
  onSegmentClick?: (segmentId: string) => void;
  /** ID of the currently-selected segment (drawn with a ring). */
  selectedSegmentId?: string | null;
}

const STATE_COLOURS: Record<StationState, { fill: string; ring: string }> = {
  normal: { fill: "#10b981", ring: "#064e3b" },
  watch: { fill: "#f59e0b", ring: "#78350f" },
  active: { fill: "#ef4444", ring: "#7f1d1d" },
};

/**
 * Pure helper: classify a segment's current state from the incidents list.
 * Exported for unit testing.
 */
export function classifySegmentState(
  segmentId: string,
  incidents: Pick<IncidentDto, "segmentId" | "status">[],
): StationState {
  const here = incidents.filter((i) => i.segmentId === segmentId);
  if (here.length === 0) return "normal";
  if (
    here.some(
      (i) =>
        i.status === "ACTIVE" ||
        i.status === "ACKNOWLEDGED" ||
        i.status === "INVESTIGATING",
    )
  ) {
    return "active";
  }
  if (here.some((i) => i.status === "DRAFT")) return "watch";
  return "normal"; // only RESOLVED here
}

/**
 * Resolve a station's icon kind from the landmark KPs.
 * Valve > scraper > default precedence (rare to have both at same KP,
 * but if there are, valve wins for visual clarity).
 */
export function resolveStationKind(
  startChainage: number,
  landmarks: SchematicLandmarks | undefined,
  toleranceKm = 5,
): StationKind {
  if (!landmarks) return "default";
  const matches = (kps: number[]) =>
    kps.some((kp) => Math.abs(startChainage - kp) <= toleranceKm);
  if (matches(landmarks.valveKps)) return "valve";
  if (matches(landmarks.scraperKps)) return "scraper";
  return "default";
}

export function SchematicCorridor({
  segments,
  incidents,
  landmarks,
  onSegmentClick,
  selectedSegmentId,
}: SchematicCorridorProps) {
  const ordered = useMemo(
    () => [...segments].sort((a, b) => a.startChainage - b.startChainage),
    [segments],
  );

  if (ordered.length === 0) {
    return (
      <section
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-xs text-slate-500 italic"
        data-testid="schematic-corridor-empty"
      >
        No segments to render. Seed the corridor first via{" "}
        <code className="text-slate-300">pnpm db:seed:pgu-southern</code>.
      </section>
    );
  }

  // 100% width, station spacing computed from chainage distance
  const minKp = ordered[0]!.startChainage;
  const maxKp = ordered[ordered.length - 1]!.startChainage;
  const range = Math.max(1, maxKp - minKp);

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 overflow-x-auto"
      data-testid="schematic-corridor"
    >
      <header className="flex items-center justify-between mb-4 text-xs">
        <span className="uppercase tracking-wide text-slate-300 font-semibold">
          Pipeline schematic
        </span>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <LegendDot fill={STATE_COLOURS.normal.fill} label="normal" />
          <LegendDot fill={STATE_COLOURS.watch.fill} label="watch" />
          <LegendDot fill={STATE_COLOURS.active.fill} label="active" />
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1">
            <ValveIcon size={11} />
            valve
          </span>
          <span className="inline-flex items-center gap-1">
            <ScraperIcon size={11} />
            scraper
          </span>
        </div>
      </header>

      <div className="relative" style={{ minWidth: 720, height: 110 }}>
        {/* Pipeline line */}
        <div
          className="absolute top-[35px] left-0 right-0 h-1 bg-slate-700 rounded-full"
          aria-hidden
        />
        {/* Stations */}
        {ordered.map((seg) => {
          const state = classifySegmentState(seg.segmentId, incidents);
          const kind = resolveStationKind(seg.startChainage, landmarks);
          const colour = STATE_COLOURS[state];
          const positionPct =
            ((seg.startChainage - minKp) / range) * 100;
          const isSelected = selectedSegmentId === seg.segmentId;
          const incidentsHere = incidents.filter(
            (i) => i.segmentId === seg.segmentId,
          ).length;
          return (
            <button
              key={seg.segmentId}
              type="button"
              onClick={() => onSegmentClick?.(seg.segmentId)}
              data-testid={`schematic-station-${seg.segmentId}`}
              data-station-state={state}
              data-station-kind={kind}
              title={`${seg.segmentId} · KP-${seg.startChainage} · ${state}`}
              className={
                `absolute flex flex-col items-center text-[10px] -translate-x-1/2 ` +
                `transition group hover:z-10`
              }
              style={{ left: `${positionPct}%`, top: 12 }}
            >
              <StationIcon
                kind={kind}
                state={state}
                selected={isSelected}
                colour={colour}
              />
              <span className="mt-2 text-slate-400 font-mono whitespace-nowrap">
                {seg.segmentId}
              </span>
              {incidentsHere > 0 && (
                <span
                  className={
                    `text-[9px] font-mono ` +
                    (state === "active" ? "text-rose-300" : "text-amber-300")
                  }
                >
                  {incidentsHere} active
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function LegendDot({ fill, label }: { fill: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block size-2.5 rounded-full"
        style={{ background: fill }}
      />
      {label}
    </span>
  );
}

function StationIcon({
  kind,
  state,
  selected,
  colour,
}: {
  kind: StationKind;
  state: StationState;
  selected: boolean;
  colour: { fill: string; ring: string };
}) {
  const ring = selected ? "#5eead4" : colour.ring;
  if (kind === "valve") {
    return (
      <svg width={22} height={22} viewBox="0 0 22 22">
        <rect
          x={2}
          y={2}
          width={18}
          height={18}
          fill={colour.fill}
          stroke={ring}
          strokeWidth={2}
          transform="rotate(45 11 11)"
          rx={1}
        />
      </svg>
    );
  }
  if (kind === "scraper") {
    return (
      <svg width={22} height={22} viewBox="0 0 22 22">
        <circle
          cx={11}
          cy={11}
          r={9}
          fill={colour.fill}
          stroke={ring}
          strokeWidth={2}
        />
        <circle
          cx={11}
          cy={11}
          r={4}
          fill="none"
          stroke="#0f172a"
          strokeWidth={1.5}
        />
      </svg>
    );
  }
  return (
    <svg width={20} height={20} viewBox="0 0 20 20">
      <circle
        cx={10}
        cy={10}
        r={7}
        fill={colour.fill}
        stroke={ring}
        strokeWidth={2}
      />
      {state === "active" && (
        <circle
          cx={10}
          cy={10}
          r={9}
          fill="none"
          stroke={colour.fill}
          strokeOpacity={0.4}
          strokeWidth={1.5}
        >
          <animate
            attributeName="r"
            from="7"
            to="11"
            dur="1.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="stroke-opacity"
            from="0.6"
            to="0"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
      )}
    </svg>
  );
}

function ValveIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12">
      <rect
        x={2}
        y={2}
        width={8}
        height={8}
        fill="#94a3b8"
        transform="rotate(45 6 6)"
      />
    </svg>
  );
}

function ScraperIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12">
      <circle cx={6} cy={6} r={5} fill="#94a3b8" />
      <circle cx={6} cy={6} r={2} fill="#0f172a" />
    </svg>
  );
}
